import { apiFetch } from './http';

export interface ProductDefinition {
  productId: string;
  productName: string;
  vendor: 'gdna' | 'third-party';
  description: string;
  requiredParameters: ParameterDefinition[];
  trustPrincipal: string;
  cfnTemplateUrl?: string;
  hasValidation: boolean;
  externalUrl?: string;
}

export interface ParameterDefinition {
  name: string;
  label: string;
  description: string;
  type: 'string' | 'arn' | 'account-id' | 'uuid';
  required: boolean;
  autoGenerated?: boolean;
}

export type DeploymentStatus =
  | 'pending'
  | 'parameters_set'
  | 'cfn_launched'
  | 'deployed'
  | 'integrated'
  | 'validation_failed'
  | 'failed'
  | 'removed';

export interface RoleDeployment {
  tenantId: string;
  productId: string;
  productName: string;
  vendor: 'gdna' | 'third-party';
  status: DeploymentStatus;
  parameters: Record<string, string>;
  stackOutputs: Record<string, string>;
  cfnLaunchUrl?: string;
  validated: boolean;
  validatedAt?: string;
  createdAt: string;
  updatedAt: string;
  notes?: string;
  customDomain?: string;
  postDeployStatus?: string;
  postDeploySteps?: Record<string, string>;
}

export interface DeploymentsResponse {
  deployments: RoleDeployment[];
  catalog: Record<string, ProductDefinition>;
}


export async function fetchDeployments(tenantId: string): Promise<DeploymentsResponse> {
  return apiFetch<DeploymentsResponse>(`/ops/tenants/${tenantId}/deployments`);
}

export async function createDeployment(
  tenantId: string,
  productId: string,
  parameters: Record<string, string>,
  notes?: string
): Promise<RoleDeployment> {
  return apiFetch<RoleDeployment>(`/ops/tenants/${tenantId}/deployments`, {
    method: 'POST',
    body: JSON.stringify({ productId, parameters, notes }),
  });
}

/**
 * Provision a SaaS-hosted deployment with no customer-account role required.
 * Upserts an `integrated` DEPLOYMENT record against the shared gdna stack.
 * Role deployment can be layered on later as an optional enhance motion.
 */
export async function provisionSaasTenant(
  tenantId: string,
  productId: string,
  stackOutputs: Record<string, string>,
  notes?: string
): Promise<RoleDeployment> {
  return apiFetch<RoleDeployment>(`/ops/tenants/${tenantId}/deployments`, {
    method: 'POST',
    body: JSON.stringify({ productId, parameters: {}, hostingMode: 'saas-hosted', stackOutputs, notes }),
  });
}

export async function updateDeployment(
  tenantId: string,
  productId: string,
  updates: Partial<{
    status: DeploymentStatus;
    parameters: Record<string, string>;
    stackOutputs: Record<string, string>;
    cfnLaunchUrl: string;
    validated: boolean;
    notes: string;
  }>
): Promise<RoleDeployment> {
  return apiFetch<RoleDeployment>(`/ops/tenants/${tenantId}/deployments/${productId}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

export async function fetchCatalog(): Promise<{ catalog: Record<string, ProductDefinition> }> {
  return apiFetch<{ catalog: Record<string, ProductDefinition> }>('/ops/catalog');
}

export async function getLaunchUrl(
  tenantId: string,
  productId: string
): Promise<{ launchUrl: string | null; presignedTemplateUrl?: string; expiresIn?: number }> {
  return apiFetch<{ launchUrl: string | null }>(`/ops/tenants/${tenantId}/deployments/${productId}/launch-url`);
}

export async function validateDeployment(
  tenantId: string,
  productId: string,
  customerAccountId: string
): Promise<{ validated: boolean; status: string; roleArn: string; customerAccountId: string; message: string }> {
  return apiFetch(`/ops/tenants/${tenantId}/deployments/${productId}/validate`, {
    method: 'POST',
    body: JSON.stringify({ customerAccountId }),
  });
}

export async function removeDeployment(
  tenantId: string,
  productId: string
): Promise<{ status: string; stackDeleted: boolean; message: string }> {
  return apiFetch(`/ops/tenants/${tenantId}/deployments/${productId}`, {
    method: 'DELETE',
  });
}
